/* OnSumn — Flows Search: GPS bar + type-ahead + hub quick-picks.
   Phase-1 lens (T8): consumed CONTEXTUALLY — mounted inside the set-home
   prompt card and the CHANGE HOME popover instead of always-on. Component API
   is stable; `gpsStatus` accepts the §1 home states alongside the legacy
   values, and `hideStatusBar` (optional) suppresses the WYA?/status bar when
   mounted in the popover (the bar belongs to the prompt context only). */

function FlowsSearch({ stations, activeStation, onSelectStation, gpsStatus, darkMode, radius, hideStatusBar }) {
  const [query, setQuery] = React.useState('');
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [hoveredIdx, setHoveredIdx] = React.useState(-1);
  const [hoveredHub, setHoveredHub] = React.useState(null);
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const textColor = darkMode ? '#fff' : '#0A0A0A';
  // muted/subtle are AA text floors (4.5:1) on the worst surface each renders on
  // (prompt card, popover, hovered dropdown row). Don't lower them.
  const mutedColor = darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)';
  const subtleColor = darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.55)';
  const borderColor = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  /* Derive system info for a station */
  const getSystem = (station) => {
    if (!station || !station.system) return { label: '?', color: '#888' };
    const info = window.SYSTEM_INFO && window.SYSTEM_INFO[station.system];
    return info || { label: station.system, color: '#888' };
  };

  /* Display-name transform (§7) — pure display, data untouched. */
  const displayName = (s) => (window.displayStationName ? window.displayStationName(s) : (s ? s.name : ''));

  /* Twin collapse (§7 → B10-2): ONE search entry per PHYSICAL station, deduped
     on the data-layer canonicalId from stations.json (canonicalId ?? id — a
     canonicalId-absent snapshot degrades to raw entries; deploy-skew-safe,
     never a mass collapse). Replaces the collapseTwins name heuristic, whose
     real scale was 3 same-name clusters (the "65 direction-twin pairs" was
     stale fiction) and which structurally MISSED 4th & Brannan ("Fourth
     Street & Brannan" vs "4th & Brannan" diverge after direction-strip). The
     entry is the CANONICAL station object (id === canonicalId), so the shown
     name is the canonical's — Brannan surfaces the vernacular. */
  const searchable = React.useMemo(() => {
    const list = stations || [];
    const seen = new Set();
    const out = [];
    for (const s of list) {
      if (!s) continue;
      const key = s.canonicalId ?? s.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s.id === key ? s : (list.find((c) => c && c.id === key) || s));
    }
    return out;
  }, [stations]);

  /* Filter stations by query — matches the DISPLAYED name plus raw/city/system. */
  const filtered = React.useMemo(() => {
    if (query.length < 2) return [];
    const q = query.toLowerCase();
    return (searchable || [])
      .filter(s =>
        (displayName(s).toLowerCase().includes(q)) ||
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.city && s.city.toLowerCase().includes(q)) ||
        (s.system && s.system.toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [query, searchable]);

  /* Show dropdown when we have results */
  React.useEffect(() => {
    setShowDropdown(filtered.length > 0);
    setHoveredIdx(-1);
  }, [filtered]);

  /* Close dropdown on outside click */
  React.useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Keyboard navigation in dropdown */
  const handleKeyDown = (e) => {
    if (!showDropdown || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoveredIdx(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoveredIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && hoveredIdx >= 0) {
      e.preventDefault();
      handleSelect(filtered[hoveredIdx]);
    } else if (e.key === 'Escape') {
      // Contain the Esc: when this component is mounted in the set-home prompt
      // card (no-home states), FlowsApp has a native document keydown listener
      // that dismisses the open desktop detail panel. An Esc that only means
      // "close the search dropdown" must not bubble up and ALSO close that panel.
      // (Reached only while the dropdown is open — the guard above early-returns
      // otherwise, so a closed-dropdown Esc still passes through to the panel.)
      e.stopPropagation();
      setShowDropdown(false);
      setQuery('');
    }
  };

  const handleSelect = (station) => {
    onSelectStation(station);
    setQuery('');
    setShowDropdown(false);
    if (inputRef.current) inputRef.current.blur();
  };

  /* Hub stations */
  const hubStations = React.useMemo(() => {
    const hubIds = window.HUB_STATIONS || [];
    if (!stations || !hubIds.length) return [];
    return hubIds
      .map(id => (stations || []).find(s => s.id === id))
      .filter(Boolean);
  }, [stations]);

  const showHubs = !activeStation && gpsStatus !== 'requesting' && gpsStatus !== 'locating' && hubStations.length > 0;
  const hubsCollapsed = query.length > 0;

  /* ── GPS Status Bar — accepts legacy ('requesting'/'located') and §1 home
        states ('locating'/'set'); anything else idles as WYA? ── */
  const renderGpsBar = () => {
    if (hideStatusBar) return null;
    let content;

    if ((gpsStatus === 'located' || gpsStatus === 'set') && activeStation) {
      const sys = getSystem(activeStation);
      content = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Pulsing orange dot */}
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'var(--international-orange)',
            display: 'inline-block', flexShrink: 0,
            animation: 'flowsSearchPulse 2s ease-in-out infinite',
          }} />
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: textColor,
          }}>
            Near {displayName(activeStation)}
          </span>
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600,
            color: sys.color, letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>{sys.label}</span>
          {activeStation.city && (
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 500,
              color: mutedColor,
            }}>{activeStation.city}</span>
          )}
        </div>
      );
    } else if (gpsStatus === 'requesting' || gpsStatus === 'locating') {
      content = (
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: mutedColor,
          animation: 'flowsSearchFindingPulse 1.8s ease-in-out infinite',
        }}>
          Finding your stop...
        </span>
      );
    } else {
      content = (
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: mutedColor,
        }}>
          WYA?
        </span>
      );
    }

    return (
      <div style={{ marginBottom: 10, padding: '0 2px' }}>
        {content}
      </div>
    );
  };

  /* ── Inject pulse keyframes ── */
  React.useEffect(() => {
    const styleId = 'flows-search-pulse-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes flowsSearchPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes flowsSearchFindingPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes flowsDropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .flows-search-drop { animation: none !important; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  /* ── System color dot helper ── */
  const SystemDot = ({ station, size }) => {
    const sys = getSystem(station);
    return (
      <span style={{
        width: size || 8, height: size || 8, borderRadius: '50%',
        background: sys.color, display: 'inline-block', flexShrink: 0,
      }} />
    );
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* GPS Status */}
      {renderGpsBar()}

      {/* Search Input */}
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { setFocused(true); if (filtered.length > 0) setShowDropdown(true); }}
          onBlur={() => { setFocused(false); }}
          placeholder="Find your station..."
          aria-label="Search transit stations"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '12px 16px',
            minHeight: 44,
            borderRadius: Math.min(radius, 12),
            border: focused
              ? '1.5px solid var(--international-orange)'
              : (darkMode ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid rgba(0,0,0,0.08)'),
            ...glassStyle(darkMode, 'btn'),
            color: textColor,
            fontFamily: 'var(--font-body)',
            // 16px floor — iOS Safari zooms when focused inputs are smaller.
            fontSize: 16,
            fontWeight: 500,
            outline: 'none',
            boxShadow: focused
              ? '0 0 0 4px rgba(255,79,0,0.13), 0 10px 30px rgba(0,0,0,0.28)'
              : (darkMode ? '0 2px 10px rgba(0,0,0,0.18)' : '0 2px 10px rgba(0,0,0,0.05)'),
            transition: 'border-color 0.2s ease, box-shadow 0.28s cubic-bezier(0.16,1,0.3,1)',
          }}
        />
        {/* Search icon */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mutedColor}
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{
            position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}>
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35"/>
        </svg>
      </div>

      {/* Dropdown Results */}
      {showDropdown && filtered.length > 0 && (
        <div
          role="listbox"
          aria-label="Station search results"
          className="flows-search-drop"
          style={{
            position: 'absolute', left: 0, right: 0,
            top: '100%', marginTop: 8, zIndex: 100,
            ...glassStyle(darkMode, 'panel'),
            // Near-solid surface: the dropdown floats over the detailed,
            // colorful transit map, so results must stay legible without
            // relying on backdrop-filter for contrast (WCAG AA). Drop the
            // glass blur entirely — over a GPU-composited map it can
            // mis-render the panel as see-through, and the solid bg makes it
            // redundant anyway.
            background: darkMode ? 'rgba(13,15,15,0.975)' : 'rgba(250,250,250,0.98)',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: `1px solid ${borderColor}`,
            borderRadius: Math.min(radius, 12),
            overflow: 'hidden',
            maxHeight: 320,
            overflowY: 'auto',
            boxShadow: darkMode
              ? '0 16px 44px rgba(0,0,0,0.5)'
              : '0 16px 44px rgba(0,0,0,0.16)',
            animation: 'flowsDropIn 0.2s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {filtered.map((station, idx) => {
            const sys = getSystem(station);
            const isHighlighted = idx === hoveredIdx;
            return (
              <button
                key={station.id}
                role="option"
                aria-selected={isHighlighted}
                onClick={() => handleSelect(station)}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(-1)}
                onFocus={() => setHoveredIdx(idx)}
                onBlur={() => setHoveredIdx(-1)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', textAlign: 'left',
                  padding: '10px 14px', minHeight: 44,
                  border: 'none', cursor: 'pointer',
                  background: isHighlighted
                    ? (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
                    : 'transparent',
                  transition: 'background 0.15s ease',
                }}
              >
                <SystemDot station={station} size={8} />
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  color: textColor, flex: 1,
                }}>
                  {displayName(station)}
                </span>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600,
                  color: sys.color, letterSpacing: '0.04em', textTransform: 'uppercase',
                }}>{sys.label}</span>
                {station.city && (
                  <span style={{
                    fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 500,
                    color: subtleColor,
                  }}>{station.city}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Hub Quick-Picks — collapse the instant the user types so the search
          dropdown owns the space (fixes the "crowded while typing" complaint).
          Height animates via grid-template-rows, not height, per motion rules. */}
      {showHubs && (
        <div style={{
          display: 'grid',
          gridTemplateRows: hubsCollapsed ? '0fr' : '1fr',
          opacity: hubsCollapsed ? 0 : 1,
          pointerEvents: hubsCollapsed ? 'none' : 'auto',
          // visibility:hidden also drops the chips from keyboard tab order when
          // collapsed (WCAG — no focusable elements in an aria-hidden subtree).
          // Delay it to the end of the collapse so the fade still plays.
          visibility: hubsCollapsed ? 'hidden' : 'visible',
          transition: hubsCollapsed
            ? 'grid-template-rows 0.34s cubic-bezier(0.16,1,0.3,1), opacity 0.2s ease, visibility 0s linear 0.34s'
            : 'grid-template-rows 0.34s cubic-bezier(0.16,1,0.3,1), opacity 0.2s ease',
        }} aria-hidden={hubsCollapsed}>
          <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div style={{ paddingTop: 16 }}>
          <label style={{
            fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: subtleColor,
            display: 'block', marginBottom: 8,
          }}>Start Here</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {hubStations.map(station => {
              const sys = getSystem(station);
              const isHovered = hoveredHub === station.id;
              return (
                <button
                  key={station.id}
                  onClick={() => handleSelect(station)}
                  onMouseEnter={() => setHoveredHub(station.id)}
                  onMouseLeave={() => setHoveredHub(null)}
                  onFocus={() => setHoveredHub(station.id)}
                  onBlur={() => setHoveredHub(null)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', minHeight: 36,
                    borderRadius: 100,
                    fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-body)',
                    letterSpacing: '0.04em',
                    cursor: 'pointer',
                    border: isHovered
                      ? '1.5px solid var(--international-orange)'
                      : (darkMode ? '1.5px solid rgba(255,255,255,0.12)' : '1.5px solid rgba(0,0,0,0.1)'),
                    background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    color: isHovered
                      ? 'var(--international-orange-ink)'
                      : (darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)'),
                    transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: isHovered ? 'var(--international-orange)' : sys.color,
                    display: 'inline-block', flexShrink: 0,
                    transition: 'background 0.2s ease',
                  }} />
                  {displayName(station)}
                </button>
              );
            })}
          </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

FlowsSearch.propTypes = {
  stations: PropTypes.array.isRequired,
  activeStation: PropTypes.object,
  onSelectStation: PropTypes.func.isRequired,
  // Legacy values + the §1 home states (prop name kept stable per spec §8).
  gpsStatus: PropTypes.oneOf([
    'idle', 'requesting', 'located', 'denied',
    'unset', 'locating', 'gps-suggest', 'manual-pick', 'outside-bay', 'browse', 'set',
  ]).isRequired,
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  hideStatusBar: PropTypes.bool,
};

window.FlowsSearch = FlowsSearch;
